<script lang="ts">
    import { alphabetical  } from 'radash';
    import { goto } from '$app/navigation';
    import type { AnyToAnyConfig } from "$lib/services/roveMap";
    import {
        showDirections,
        locations,
        mapInstance,
        destinationId,
        venue,
        amenities,
    } from '$lib/stores';
    import { t } from '$lib/translations';

    let start = "";
    let end = "";
    let startError = '';
    let endError = '';
    let handicap = false;
    let allLocations: Destination[] = [];

    interface Destination {
        type: string;
        id: string;
        display: string;
    }

    $: if($locations || $amenities) {
        allLocations = [];
        $locations.forEach((location) => {
            allLocations.push({
                type: 'location',
                id: `location|${location.id}`,
                display: location.name,
            })
        });
        $amenities.forEach((amenity) => {
            allLocations.push({
                type: 'amenity',
                id: `amenity|${amenity.id}`,
                display: amenity.name,
            })
        });
        allLocations = alphabetical (allLocations, f => f.display.toLowerCase());
    }

    $: if($destinationId) {
        end = $destinationId;
        $destinationId = undefined;
    }

    const switchLocations = () => {
      const temp = start;
      start = end;
      end = temp;
    };

    const reset = () => {
        start = ""
        end = ""
        if ($mapInstance) {
            $mapInstance.reset();
        }
    };

    const getDirections = () => {
        startError = '';
        endError = '';
        if (start <= "") {
            startError = 'required';
        }
        if (end <= "") {
            endError = 'required';
        }
        if (endError === '' && start === end) {
            endError = 'Select different locations';
        }
        if (startError === '' && endError === '') {
            if ($mapInstance) {
                const el = document.getElementById('location-details');
                if (el) el.remove();

                goto(`/venues/${$venue.id}`);
                const config: AnyToAnyConfig = {
                    startIdString: start,
                    endIdString: end,
                    handicapMode: handicap,
                    paddingRight: 400
                }
                $mapInstance.routeAnyToAny(config);
            }
        }
    }
</script>

<div id="directionsSelector" class:hideDirectionsBox={!$showDirections} class:showDirectionsBox={$showDirections}>
    <div>
        <label class="inline-flex items-center cursor-pointer">
            <input type="checkbox" value="" class="sr-only peer" bind:checked={handicap}>
            <div class="relative w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                <img src="/handicap.svg" />
            </span>
        </label>
    </div>
    <div class="grid grid-cols-[1fr_auto] items-center gap-1">
        <div class="grid grid-rows-2 grid-cols-[auto_1fr] items-center gap-2">
            <label for="countries" class="mr-4 text-sm font-medium text-gray-900">From</label>
            <div>
                <select id="countries" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" bind:value={start}>
                    <option value="">{ $t('content.selectStart') }</option>
                    {#each allLocations as location}
                        <option value={location.id}>{location.display}</option>
                    {/each}
                </select>
                {#if startError}
                    <div class="text-red-500 text-xs">{startError}</div>
                {/if}
            </div>
            <label for="countries" class="mr-4 text-sm font-medium text-gray-900">To</label>
            <div>
                <select id="countries" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" bind:value={end}>
                    <option value="">{ $t('content.selectDest') }</option>
                    {#each allLocations as location}
                        <option value={location.id}>{location.display}</option>
                    {/each}
                </select>
                {#if endError}
                    <div class="text-red-500 text-xs">{endError}</div>
                {/if}
            </div>
        </div>
        <div on:click={switchLocations} on:keypress={switchLocations} >
            <img alt="reverse locations" src="/reverse.svg" class="w-4"/>
        </div>
    </div>
    <div class="mt-4 grid grid-cols-2 mr-5 gap-2">
        <button type="button" on:click={reset} on:keypress={reset} class="bg-[#f1dde2] text-white w-full py-2.5 px-5 me-2 mb-2 text-sm font-medium focus:outline-none rounded-lg border border-gray-200 focus:z-10 focus:ring-4 focus:ring-gray-100">
            { $t('content.reset') }
        </button>
        <button type="button" class="main-color-background w-full text-white focus:ring-4 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2" on:click={getDirections}>
            { $t('content.getDirections') }
        </button>
    </div>
</div>

<style lang="postcss">
    :global(#directionsSelector) {
        -webkit-box-shadow: 0px 0px 28px 3px rgba(0,0,0,0.5);
        -moz-box-shadow: 0px 0px 28px 3px rgba(0,0,0,0.5);
        box-shadow: 0px 0px 28px 3px rgba(0,0,0,0.5);
        @apply fixed z-[60] bg-white rounded-3xl p-4;
        transition: top 0.5s;
        right: 30vw;
    }
    :global(.leftMenu #directionsSelector) {
        right: unset;
        left: 30vw;
    }

    .hideDirectionsBox {
        top: -50vh;
    }
    .showDirectionsBox {
        top: 4vh;
    }
</style>
